import {
  Timeline, TimelineItem, TimelineSeparator, TimelineConnector,
  TimelineContent, TimelineOppositeContent, TimelineDot
} from '@mui/lab';
import {
  Card, CardHeader, Typography, Avatar, Stack, Grid, Box,
  FormGroup, FormControlLabel, Switch
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useMemo, useState } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);

// --------------------- Funções Utilitárias ------------------------

const findOptionLabelIcon = (value, options) => {
  for (const option of options) {
    if (option.value === value) return { label: option.label, icon: option.icon };
    if (option.options?.length) {
      const found = findOptionLabelIcon(value, option.options);
      if (found) return found;
    }
  }
  return null;
};

const getLabelAndIcon = (reason, dialogOptions) => {
  for (const key of Object.keys(dialogOptions)) {
    const found = findOptionLabelIcon(reason, dialogOptions[key].options);
    if (found) return found;
  }
  return { label: reason || 'Sem motivo', icon: null };
};

const groupByUser = (operations) => {
  return operations.reduce((acc, op) => {
    if (!acc[op.user_uuid]) acc[op.user_uuid] = [];
    acc[op.user_uuid].push(op);
    return acc;
  }, {});
};

const isBreakLabel = (label) => /almo[cç]o|janta|lanche|banheiro|aguardando|finalizar dia/i.test(label);

// ---------------------- Componente Principal -----------------------

const TimelineUsers = ({ operations, dialogOptions, sellers }) => {
  const S3_URL = process.env.REACT_APP_S3_URL;
  const userTimezone = dayjs.tz.guess();
  const [showAguardando, setShowAguardando] = useState(false);
  const [showBreaks, setShowBreaks] = useState(false);
  const [showEndOfDay, setShowEndOfDay] = useState(false);


  // Determina a data de referência mais recente
  const referenceDate = useMemo(() => {
    if (!operations.length) return null;
    return operations
      .map(op => dayjs.utc(op.started_at).tz(userTimezone).startOf('day'))
      .sort((a, b) => b.valueOf() - a.valueOf())[0];
  }, [operations, userTimezone]);

  // Filtra apenas operações da data de referência
  const filteredOperations = useMemo(() => {
    if (!referenceDate) return [];
    return operations.filter(op =>
      dayjs.utc(op.started_at).tz(userTimezone).startOf('day').isSame(referenceDate)
    );
  }, [operations, referenceDate, userTimezone]);

  const groupedOps = useMemo(() => groupByUser(filteredOperations), [filteredOperations]);

  const userRates = useMemo(() => {
    return Object.entries(groupedOps).map(([userId, ops]) => {
      const userOps = [];

      ops.forEach((op) => {
        const started = dayjs.utc(op.started_at).tz(userTimezone);
        const finished = op.finished_at ? dayjs.utc(op.finished_at).tz(userTimezone) : null;
        const reason = op.start_reason || op.finish_reason;

        let { label, icon } = getLabelAndIcon(reason, dialogOptions);

        if (op.from_status === 'unavailable' && op.to_status === 'available') {
          userOps.push({ ...op, started, finished, label: 'Aguardando', icon: 'HourglassEmpty' });
          return;
        }

        if (op.from_status === 'inService' && op.to_status === 'available') {
          label = 'Aguardando';
          icon = 'HourglassEmpty';
        }

        if (label === undefined && isBreakLabel(reason)) {
          label = 'Retornou';
          icon = 'Logout';
        }

        userOps.push({ ...op, started, finished, label, icon });
      });

      const relevant = userOps.filter(
        (op) =>
          op.from_status === 'available' &&
          op.to_status === 'inService' &&
          op.finish_reason?.toLowerCase() !== 'banheiro'
      );

      const total = relevant.length;
      const success = relevant.filter(op => op.label === 'Sucesso').length;
      const rate = total > 0 ? (success / total) * 100 : 0;

      return { userId, rate };
    });
  }, [groupedOps, dialogOptions, userTimezone]);

  const topUserIds = userRates
    .slice()
    .sort((a, b) => b.rate - a.rate)
    .slice(0, 3)
    .map((item) => item.userId);

  return (
    <Card sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={`Andamento em ${referenceDate?.format('DD/MM/YY') || '-'}`}
        action={
          <FormGroup row>
            <FormControlLabel
              control={
                <Switch
                  checked={showAguardando}
                  onChange={() => setShowAguardando(prev => !prev)}
                />
              }
              label="Esperas"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showBreaks}
                  onChange={() => setShowBreaks(prev => !prev) || setShowEndOfDay(prev => !prev)}
                />
              }
              label="Saídas"
            />
          </FormGroup>
        }
      />

      <Grid container spacing={4}>
        {Object.entries(groupedOps)
          .sort(([idA], [idB]) => {
            const rateA = userRates.find(u => u.userId === idA)?.rate || 0;
            const rateB = userRates.find(u => u.userId === idB)?.rate || 0;
            return rateB - rateA;
          })
          .map(([userId, userOpsRaw]) => {
            const seller = sellers.find((s) => s.uuid === userId);
            const sellerName = seller?.name || 'Desconhecido';
            const avatarUrl = seller?.avatar_url ? `${S3_URL}/img/user/${seller.avatar_url}` : null;

            let insertRetornouNext = false;
            const userOps = [];

            userOpsRaw.forEach((op) => {
              const started = dayjs.utc(op.started_at).tz(userTimezone);
              const finished = op.finished_at ? dayjs.utc(op.finished_at).tz(userTimezone) : null;
              const reason = op.start_reason || op.finish_reason;
              let { label, icon } = getLabelAndIcon(reason, dialogOptions);

              if (op.from_status === 'unavailable' && op.to_status === 'available') {
                userOps.push({
                  ...op,
                  started,
                  finished,
                  label: 'Aguardando',
                  icon: 'HourglassEmpty',
                });
                return;
              }

              if (op.from_status === 'inService' && op.to_status === 'available') {
                label = 'Aguardando';
                icon = 'HourglassEmpty';
              }

              if (insertRetornouNext && !label && !icon) {
                label = 'Retornou';
                icon = 'Logout';
              }

              insertRetornouNext = isBreakLabel(label);

              userOps.push({ ...op, started, finished, label, icon });
            });

            const relevantOps = userOps.filter(
              (op) =>
                op.from_status === 'available' &&
                op.to_status === 'inService' &&
                op.finish_reason?.toLowerCase() !== 'banheiro'
            );

            const totalOps = relevantOps.length;
            const successOps = relevantOps.filter(op => op.label === 'Sucesso').length;
            const conversionRate = totalOps > 0 ? Math.round((successOps / totalOps) * 100) : 0;

            // Tempo total em minutos
            const tempoTotalEmAtendimento = userOps
              .filter(op => op.from_status === 'available' && op.to_status === 'inService' && op.finished)
              .reduce((acc, op) => acc + op.finished.diff(op.started, 'minute'), 0);

            const tempoTotalAguardando = userOps
              .filter(op => op.label === 'Aguardando' && op.finished)
              .reduce((acc, op) => acc + op.finished.diff(op.started, 'minute'), 0);

            const totalAtendimentos = userOps.filter(
              op => op.from_status === 'available' && op.to_status === 'inService' && op.finished
            ).length;

            const totalAguardando = userOps.filter(
              op => op.label === 'Aguardando' && op.finished
            ).length;

            const tempoMedioAtendimento = totalAtendimentos > 0
              ? Math.round(tempoTotalEmAtendimento / totalAtendimentos)
              : 0;

            const tempoMedioAguardando = totalAguardando > 0
              ? Math.round(tempoTotalAguardando / totalAguardando)
              : 0;

            const formatMinutes = (min) => {
              if (min >= 60) {
                const h = Math.floor(min / 60);
                const m = min % 60;
                return `${h}h${m > 0 ? ` ${m}min` : ''}`;
              }
              return `${min} min`;
            };

            return (
              <Grid item xs={12} md={4} key={userId}>
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                  <Box position="relative">
                    {avatarUrl && <Avatar src={avatarUrl} alt={sellerName} />}
                    {topUserIds.includes(userId) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          fontSize: 20,
                          bgcolor: 'white',
                          borderRadius: '50%',
                          px: 0.5,
                          boxShadow: '0 0 6px rgba(0,0,0,0.2)',
                        }}
                        title={
                          topUserIds.indexOf(userId) === 0
                            ? '🥇 1º lugar'
                            : topUserIds.indexOf(userId) === 1
                            ? '🥈 2º lugar'
                            : '🥉 3º lugar'
                        }
                      >
                        {topUserIds.indexOf(userId) === 0
                          ? '🥇'
                          : topUserIds.indexOf(userId) === 1
                          ? '🥈'
                          : '🥉'}
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {sellerName}
                    </Typography>

                    <Stack spacing={0.5} mt={0.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <MuiIcons.Check fontSize="small" sx={{ color: '#60c765' }} />
                        <Typography variant="caption" color="text.secondary">
                          {conversionRate}% conversão
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <MuiIcons.AccessTime fontSize="small" sx={{ color: '#1976d2' }} />
                        <Typography variant="caption" color="text.secondary">
                          {totalAtendimentos} atendimentos: {formatMinutes(tempoTotalEmAtendimento)} (média: {formatMinutes(tempoMedioAtendimento)})
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1}>
                        <MuiIcons.HourglassBottom fontSize="small" sx={{ color: '#ff9800' }} />
                        <Typography variant="caption" color="text.secondary">
                          {totalAguardando} esperas: {formatMinutes(tempoTotalAguardando)} (média: {formatMinutes(tempoMedioAguardando)})
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>


                </Stack>

                <Timeline position="right">
                  {userOps
                    .filter((op) => {
                      if (op.label === 'Aguardando') return showAguardando;
                      if (op.label === 'Finalizar dia') return showEndOfDay;
                      if (isBreakLabel(op.label) && op.label !== 'Finalizar dia') return showBreaks;
                      return true;
                    })
                    .map((op) => {
                      const Icon = op.icon && MuiIcons[op.icon] ? MuiIcons[op.icon] : MuiIcons.HelpOutline;
                      let dotColor;
                      if (op.label === 'Sucesso') {
                        dotColor = '#60c765';
                      } else if (op.label === 'Aguardando') {
                        dotColor = '#e4d24dff';
                      } else if (isBreakLabel(op.label)) {
                        dotColor = 'grey.400';
                      } else if (op.label === 'Iniciou') {
                        dotColor = 'primary.main';
                      } else if (op.label === 'Retornou') {
                        dotColor = 'info.main';
                      } else {
                        dotColor = '#ff7300';
                      }

                      return (
                        <TimelineItem key={op.id}>
                          <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3 }}>
                            {op.started.format('HH:mm')}
                          </TimelineOppositeContent>
                          <TimelineSeparator>
                            <TimelineConnector />
                            <TimelineDot sx={{ bgcolor: dotColor }}>
                              <Icon fontSize="small" />
                            </TimelineDot>
                            <TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent>
                            <Typography variant="body2">{op.label}</Typography>
                            {!['Iniciou', 'Finalizar dia'].includes(op.label) && (
                              <Typography variant="caption" color="text.secondary">
                                {op.started.format('HH:mm')} –{' '}
                                {op.finished ? op.finished.format('HH:mm') : '...'}
                                {op.finished && ` (${op.finished.diff(op.started, 'minute')} min)`}
                              </Typography>
                            )}
                          </TimelineContent>
                        </TimelineItem>
                      );
                    })}
                </Timeline>

              </Grid>
            );
          })}
      </Grid>
    </Card>
  );
};

export default TimelineUsers;
